import React, { useState, useEffect } from 'react';
import {
  Box,
  IconButton,
  Typography,
  InputAdornment,
  TextField,
  List,
  ListItem,
  ListItemText,
  Slide,
  Divider,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ViewInArIcon from '@mui/icons-material/ViewInAr'; // Import an icon for the model
import { gql, useQuery } from '@apollo/client';

const GET_LLM_TRACE_DETAIL = gql`
  query GetLlmTraceDetail($tenantId: ID!, $traceId: ID!) {
    getLlmTraceDetail(tenantId: $tenantId, traceId: $traceId) {
      id
      timestamp
      name
      latency
      cost
      totalTokens
      input
      output
      metadata
      observations {
        id
        name
        startTime
        endTime
        input
        output
        cost
        totalTokens
        model
      }
    }
  }
`;

interface TraceDetailPanelProps {
  open: boolean;
  onClose: () => void;
  tenantId?: string;
  traceId?: string;
}

interface DisplayItem {
  id: string;
  name: string;
  time: string;
  tokens: number;
  cost: number;
  input: string | null;
  output: string | null;
  metadata: string;
  timestamp: string;
  model?: string; // Add optional model field
}

// Helper function to convert objects to formatted JSON strings
const stringifyIfObject = (value: any): string | null => {
  if (typeof value === 'object' && value !== null) {
    return JSON.stringify(value, null, 2); // Pretty-print JSON
  }
  return value;
};


const InfoChip: React.FC<{ icon: React.ElementType; label: React.ReactNode }> = ({ icon: Icon, label }) => (
  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, bgcolor: '#F2F4F7', borderRadius: '16px', px: 1.25, py: 0.25, border: '1px solid #EAECF0' }}>
    <Icon sx={{ fontSize: '16px', color: '#475467' }} />
    <Typography sx={{ fontSize: '12px', color: '#344054', fontWeight: 500, fontFamily: 'Inter' }}>{label}</Typography>
    <InfoOutlinedIcon sx={{ fontSize: '13px', color: '#0064FF', cursor: 'pointer' }} />
  </Box>
);

const DetailCard: React.FC<{ title: string; content: React.ReactNode, contentColor?: string }> = ({ title, content, contentColor = '#475467' }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    const textToCopy = String(content || '');
    
    navigator.clipboard.writeText(textToCopy).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(err => {
      console.error('Failed to copy text: ', err);
    });
  };

  return (
    <Box sx={{
      border: '1px solid #EAECF0',
      borderRadius: '12px',
      mb: 2,
      position: 'relative',
      overflow: 'hidden',
    }}>
      <Box sx={{
          fontSize:'10px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        p: '12px 16px',
        background: '#F3F3F3',
        borderBottom: '1px solid #EAECF0',
      }}>
        <Typography sx={{
          fontWeight: 500,
          color: '#101828',
          fontFamily: "'Roboto Mono', monospace",
          fontSize: '12px'
        }}>
          {title}
        </Typography>
        <Tooltip title={copied ? "Copied!" : "Copy"} placement="top">
          <IconButton size="small" sx={{ color: '#667085' }} onClick={handleCopy}>
            <ContentCopyIcon sx={{ fontSize: '14px',color:'#666666'}} />
          </IconButton>
        </Tooltip>
      </Box>

      <Box sx={{
        p: '16px',
        background: '#F9FAFB',
      }}>
        <Typography sx={{
          color: contentColor,
          whiteSpace: 'pre-wrap',
          fontFamily: "'Roboto Mono', monospace",
          fontSize: '14px'
        }}>
          {content || 'N/A'}
        </Typography>
      </Box>
    </Box>
  );
};

const formatTimestamp = (isoString: string): string => {
    if (!isoString) return '';
    const date = new Date(isoString);
    const formattedDate = date.toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
    });
    const formattedTime = date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
    });
    return `${formattedDate} • ${formattedTime}`;
};


const TraceDetailPanel: React.FC<TraceDetailPanelProps> = ({ open, onClose, tenantId, traceId }) => {
  const { data, loading, error } = useQuery(GET_LLM_TRACE_DETAIL, {
    variables: { tenantId, traceId },
    skip: !open || !tenantId || !traceId,
  });

  const [displayItems, setDisplayItems] = useState<DisplayItem[]>([]);
  const [selectedItem, setSelectedItem] = useState<DisplayItem | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  

  useEffect(() => {
    if (data?.getLlmTraceDetail) {
      const mainTrace = data.getLlmTraceDetail;
      
      const formattedMainTrace: DisplayItem = {
        id: mainTrace.id,
        name: mainTrace.name,
        time: `${mainTrace.latency?.toFixed(2) ?? 0}s`,
        tokens: mainTrace.totalTokens ?? 0,
        cost: mainTrace.cost ?? 0,
        input: stringifyIfObject(mainTrace.input),
        output: stringifyIfObject(mainTrace.output),
        metadata: JSON.stringify(mainTrace.metadata, null, 2),
        timestamp: mainTrace.timestamp,
      };

      const formattedObservations: DisplayItem[] = (mainTrace.observations || []).map((obs: any) => {
        const latency = (new Date(obs.endTime).getTime() - new Date(obs.startTime).getTime()) / 1000;
        return {
          id: obs.id,
          name: obs.name,
          time: `${latency.toFixed(2)}s`,
          tokens: obs.totalTokens,
          cost: obs.cost ?? 0,
          input: stringifyIfObject(obs.input),
          output: stringifyIfObject(obs.output),
          metadata: 'N/A for individual observations',
          timestamp: obs.startTime,
          model: obs.model, // Map the model field
        };
      });

      const allItems = [formattedMainTrace, ...formattedObservations];
      setDisplayItems(allItems);
      setSelectedItem(allItems[0]);
    }
  }, [data]);

  const renderContent = () => {
    if (loading) {
      return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}><CircularProgress /></Box>;
    }
    if (error) {
      return <Box sx={{ p: 3 }}><Typography color="error">Error: {error.message}</Typography></Box>;
    }
    if (!selectedItem) {
      return null;
    }

    return (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '320px 1fr' }, flexGrow: 1, overflow: 'hidden', bgcolor: '#FFFFFF' }}>
          {/* Left Column (Trace List) */}
          <Box sx={{ display: 'flex', flexDirection: 'column', borderRight: '1px solid #EAECF0', overflow: 'hidden' }}>
            <Box sx={{ p: 2, borderBottom: '1px solid #EAECF0' }}>
            <TextField variant="outlined" size="small" placeholder="Search by trace name..." value={searchQuery} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)} sx={{ minWidth: '250px', '& .MuiOutlinedInput-root': { borderRadius: '10px', fontFamily: 'Inter', fontSize: '14px', height: '32px', backgroundColor: 'rgba(0, 0, 0, 0.04)', color: '#232324', fontWeight: '400', '& fieldset': { border: 0.2 } }, '& .MuiInputBase-input::placeholder': { opacity: 1, fontWeight:300, fontSize: '14px', color: '#999999' } }} InputProps={{ startAdornment: ( <InputAdornment position="start"><img src="/icons/search_icon.svg" alt="Search" style={{ width: 17, height: 17 }} /></InputAdornment>)}} />
            </Box>
            
            <List sx={{ overflowY: 'auto', p: 0, position: 'relative' }}>
              <Box sx={{ position: 'absolute', top: '28px', bottom: '28px', left: '23px', width: '1px', bgcolor: '#EAECF0', zIndex: 1 }} />
              {displayItems.map((item: DisplayItem, index: number) => {
                const isSelected = selectedItem.id === item.id;
                const isFirstItem = index === 0;
                const isLastItem = index === displayItems.length - 1;

                return (
                  <ListItem key={item.id} onClick={() => setSelectedItem(item)} sx={{ flexDirection: 'column', alignItems: 'flex-start', cursor: 'pointer', position: 'relative', py: 1.5, px: 2, borderLeft: '3px solid', borderLeftColor: isSelected ? '#0064FF' : 'transparent', borderBottom: isLastItem ? 'none' : '1px solid #EAECF0', zIndex: isFirstItem ? 2 : 'auto', backgroundColor: isFirstItem ? (isSelected ? '#F2F7FF' : '#FFFFFF') : (isSelected ? '#F2F7FF' : 'transparent'), '&:hover': { backgroundColor: isSelected ? '#F2F7FF' : '#F9FAFB', }, ...(!isFirstItem && { pl: '44px', '&::before': { content: '""', position: 'absolute', left: '23px', top: '50%', transform: 'translate(-50%, -50%)', width: '9px', height: '9px', borderRadius: '50%', bgcolor: isSelected ? '#0064FF' : '#D0D5DD', border: isSelected ? 'none' : '2px solid white', zIndex: 3, }, '&::after': { content: '""', position: 'absolute', right: `calc(100% - 20px)`, top: '50%', transform: 'translateY(-50%)', width: '21px', height: '2px', bgcolor: isSelected ? '#0064FF' : '#EAECF0', zIndex: 2, }, }), }}>
                    <ListItemText primary={item.name} primaryTypographyProps={{ fontWeight: 500, fontSize: '14px', color: '#1D2939' }} />
                    <Box component="div" sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 0.5, color: '#667085', fontSize: '12px', width: '100%' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><AccessTimeIcon sx={{ fontSize: '14px' }} /> {item.time}</Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><ConfirmationNumberOutlinedIcon sx={{ fontSize: '14px' }} /> {item.tokens} tokens</Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><CreditCardOutlinedIcon sx={{ fontSize: '14px' }} /> ${item.cost?.toFixed(6)}</Box>
                    </Box>
                  </ListItem>
                );
              })}
            </List>
          </Box>
          {/* Right Column (Details) */}
          <Box sx={{ overflowY: 'auto', p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', mb: 3 }}>
              <Typography 
                sx={{ 
                  fontWeight: 600, 
                  fontFamily: 'Inter', 
                  color: '#333333',
                  mr: 2 ,
                  fontSize: '16px'
                }}
              >
                {selectedItem.name}
              </Typography>
              <Typography 
                sx={{ 
                    fontFamily: 'Inter',
                    fontWeight: 400,
                    fontSize: '12px',
                    lineHeight: '20px',
                    color: '#666666',
                }}
              >
                {formatTimestamp(selectedItem.timestamp)}
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
              {selectedItem.model && (
                  <InfoChip icon={ViewInArIcon} label={`Model: ${selectedItem.model}`} />
              )}
              <InfoChip icon={AccessTimeIcon} label={`Latency: ${selectedItem.time}`} />
              <InfoChip icon={ConfirmationNumberOutlinedIcon} label={`Tokens: ${selectedItem.tokens}`} />
              <InfoChip icon={CreditCardOutlinedIcon} label={`Cost: $${selectedItem.cost?.toFixed(6)}`} />
            </Box>
            <Divider sx={{ my: 3 }} />
            <DetailCard title="Input" content={selectedItem.input} />
            <DetailCard title="Output" contentColor="#039855" content={selectedItem.output} />
            <DetailCard title="Metadata" content={selectedItem.metadata} />
          </Box>
        </Box>
    );
  };

  return (
    <Slide direction="left" in={open} mountOnEnter unmountOnExit>
      <Box sx={{ position: 'absolute', top: 94, right: 0, bottom: 0, width: { xs: 'calc(100% - 48px)', md: '80%', lg: '70%' }, maxWidth: '900px', zIndex: 2, display: 'flex', flexDirection: 'column', boxShadow: '0 4px 12px rgba(0,0,0,0.08)', overflow: 'hidden', border: '1px solid #EAECF0', bgcolor: 'white' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, bgcolor: '#F9FAFB', borderBottom: '1px solid #EAECF0' }}>
          <Typography sx={{ fontSize: "16px", fontWeight: 600, fontFamily: 'Inter', color: '#101828' }}>Trace Details</Typography>
          <IconButton onClick={onClose} sx={{ color: '#667085' }}><CloseIcon /></IconButton>
        </Box>
        {renderContent()}
      </Box>
    </Slide>
  );
};

export default TraceDetailPanel;



// import React, { useState, useEffect } from 'react';
// import {
//   Box,
//   IconButton,
//   Typography,
//   InputAdornment,
//   TextField,
//   List,
//   ListItem,
//   ListItemText,
//   Slide,
//   Divider,
//   CircularProgress,
//   Tooltip,
// } from '@mui/material';
// import CloseIcon from '@mui/icons-material/Close';
// import ContentCopyIcon from '@mui/icons-material/ContentCopy';
// import AccessTimeIcon from '@mui/icons-material/AccessTime';
// import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
// import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
// import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
// import { gql, useQuery } from '@apollo/client';

// const GET_LLM_TRACE_DETAIL = gql`
//   query GetLlmTraceDetail($tenantId: ID!, $traceId: ID!) {
//     getLlmTraceDetail(tenantId: $tenantId, traceId: $traceId) {
//       id
//       timestamp
//       name
//       latency
//       cost
//       totalTokens
//       input
//       output
//       metadata
//       observations {
//         id
//         name
//         startTime
//         endTime
//         input
//         output
//         cost
//         totalTokens
//       }
//     }
//   }
// `;

// interface TraceDetailPanelProps {
//   open: boolean;
//   onClose: () => void;
//   tenantId?: string;
//   traceId?: string;
// }

// interface DisplayItem {
//   id: string;
//   name: string;
//   time: string;
//   tokens: number;
//   cost: number;
//   input: string | null;
//   output: string | null;
//   metadata: string;
//   timestamp: string;
// }

// // Helper function to convert objects to formatted JSON strings
// const stringifyIfObject = (value: any): string | null => {
//   if (typeof value === 'object' && value !== null) {
//     return JSON.stringify(value, null, 2); // Pretty-print JSON
//   }
//   return value;
// };


// const InfoChip: React.FC<{ icon: React.ElementType; label: React.ReactNode }> = ({ icon: Icon, label }) => (
//   <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, bgcolor: '#F2F4F7', borderRadius: '16px', px: 1.25, py: 0.25, border: '1px solid #EAECF0' }}>
//     <Icon sx={{ fontSize: '16px', color: '#475467' }} />
//     <Typography sx={{ fontSize: '12px', color: '#344054', fontWeight: 500, fontFamily: 'Inter' }}>{label}</Typography>
//     <InfoOutlinedIcon sx={{ fontSize: '13px', color: '#0064FF', cursor: 'pointer' }} />
//   </Box>
// );

// const DetailCard: React.FC<{ title: string; content: React.ReactNode, contentColor?: string }> = ({ title, content, contentColor = '#475467' }) => {
//   const [copied, setCopied] = useState(false);

//   const handleCopy = () => {
//     const textToCopy = String(content || '');
    
//     navigator.clipboard.writeText(textToCopy).then(() => {
//       setCopied(true);
//       setTimeout(() => setCopied(false), 2000);
//     }).catch(err => {
//       console.error('Failed to copy text: ', err);
//     });
//   };

//   return (
//     <Box sx={{
//       border: '1px solid #EAECF0',
//       borderRadius: '12px',
//       mb: 2,
//       position: 'relative',
//       overflow: 'hidden',
//     }}>
//       <Box sx={{
//           fontSize:'10px',
//         display: 'flex',
//         justifyContent: 'space-between',
//         alignItems: 'center',
//         p: '12px 16px',
//         background: '#F3F3F3',
//         borderBottom: '1px solid #EAECF0',
//       }}>
//         <Typography sx={{
//           fontWeight: 500,
//           color: '#101828',
//           fontFamily: "'Roboto Mono', monospace",
//           fontSize: '12px'
//         }}>
//           {title}
//         </Typography>
//         <Tooltip title={copied ? "Copied!" : "Copy"} placement="top">
//           <IconButton size="small" sx={{ color: '#667085' }} onClick={handleCopy}>
//             <ContentCopyIcon sx={{ fontSize: '14px',color:'#666666'}} />
//           </IconButton>
//         </Tooltip>
//       </Box>

//       <Box sx={{
//         p: '16px',
//         background: '#F9FAFB',
//       }}>
//         <Typography sx={{
//           color: contentColor,
//           whiteSpace: 'pre-wrap',
//           fontFamily: "'Roboto Mono', monospace",
//           fontSize: '14px'
//         }}>
//           {content || 'N/A'}
//         </Typography>
//       </Box>
//     </Box>
//   );
// };

// const formatTimestamp = (isoString: string): string => {
//     if (!isoString) return '';
//     const date = new Date(isoString);
//     const formattedDate = date.toLocaleDateString('en-US', {
//         month: '2-digit',
//         day: '2-digit',
//         year: 'numeric',
//     });
//     const formattedTime = date.toLocaleTimeString('en-US', {
//         hour: 'numeric',
//         minute: '2-digit',
//         hour12: true,
//     });
//     return `${formattedDate} • ${formattedTime}`;
// };


// const TraceDetailPanel: React.FC<TraceDetailPanelProps> = ({ open, onClose, tenantId, traceId }) => {
//   const { data, loading, error } = useQuery(GET_LLM_TRACE_DETAIL, {
//     variables: { tenantId, traceId },
//     skip: !open || !tenantId || !traceId,
//   });

//   const [displayItems, setDisplayItems] = useState<DisplayItem[]>([]);
//   const [selectedItem, setSelectedItem] = useState<DisplayItem | null>(null);
//   const [searchQuery, setSearchQuery] = useState('');
  

//   useEffect(() => {
//     if (data?.getLlmTraceDetail) {
//       const mainTrace = data.getLlmTraceDetail;
      
//       const formattedMainTrace: DisplayItem = {
//         id: mainTrace.id,
//         name: mainTrace.name,
//         time: `${mainTrace.latency?.toFixed(2) ?? 0}s`,
//         tokens: mainTrace.totalTokens ?? 0,
//         cost: mainTrace.cost ?? 0,
//         // --- FIXED: Ensure input/output are strings ---
//         input: stringifyIfObject(mainTrace.input),
//         output: stringifyIfObject(mainTrace.output),
//         metadata: JSON.stringify(mainTrace.metadata, null, 2),
//         timestamp: mainTrace.timestamp,
//       };

//       const formattedObservations: DisplayItem[] = (mainTrace.observations || []).map((obs: any) => {
//         const latency = (new Date(obs.endTime).getTime() - new Date(obs.startTime).getTime()) / 1000;
//         return {
//           id: obs.id,
//           name: obs.name,
//           time: `${latency.toFixed(2)}s`,
//           tokens: obs.totalTokens,
//           cost: obs.cost ?? 0,
//           // --- FIXED: Ensure input/output are strings ---
//           input: stringifyIfObject(obs.input),
//           output: stringifyIfObject(obs.output),
//           metadata: 'N/A for individual observations',
//           timestamp: obs.startTime,
//         };
//       });

//       const allItems = [formattedMainTrace, ...formattedObservations];
//       setDisplayItems(allItems);
//       setSelectedItem(allItems[0]);
//     }
//   }, [data]);

//   const renderContent = () => {
//     if (loading) {
//       return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}><CircularProgress /></Box>;
//     }
//     if (error) {
//       return <Box sx={{ p: 3 }}><Typography color="error">Error: {error.message}</Typography></Box>;
//     }
//     if (!selectedItem) {
//       return null;
//     }

//     return (
//         <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '320px 1fr' }, flexGrow: 1, overflow: 'hidden', bgcolor: '#FFFFFF' }}>
//           {/* Left Column (Trace List) */}
//           <Box sx={{ display: 'flex', flexDirection: 'column', borderRight: '1px solid #EAECF0', overflow: 'hidden' }}>
//             <Box sx={{ p: 2, borderBottom: '1px solid #EAECF0' }}>
//             <TextField variant="outlined" size="small" placeholder="Search by trace name..." value={searchQuery} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)} sx={{ minWidth: '250px', '& .MuiOutlinedInput-root': { borderRadius: '10px', fontFamily: 'Inter', fontSize: '14px', height: '32px', backgroundColor: 'rgba(0, 0, 0, 0.04)', color: '#232324', fontWeight: '400', '& fieldset': { border: 0.2 } }, '& .MuiInputBase-input::placeholder': { opacity: 1, fontWeight:300, fontSize: '14px', color: '#999999' } }} InputProps={{ startAdornment: ( <InputAdornment position="start"><img src="/icons/search_icon.svg" alt="Search" style={{ width: 17, height: 17 }} /></InputAdornment>)}} />
//             </Box>
            
//             <List sx={{ overflowY: 'auto', p: 0, position: 'relative' }}>
//               <Box sx={{ position: 'absolute', top: '28px', bottom: '28px', left: '23px', width: '1px', bgcolor: '#EAECF0', zIndex: 1 }} />
//               {displayItems.map((item: DisplayItem, index: number) => {
//                 const isSelected = selectedItem.id === item.id;
//                 const isFirstItem = index === 0;
//                 const isLastItem = index === displayItems.length - 1;

//                 return (
//                   <ListItem key={item.id} onClick={() => setSelectedItem(item)} sx={{ flexDirection: 'column', alignItems: 'flex-start', cursor: 'pointer', position: 'relative', py: 1.5, px: 2, borderLeft: '3px solid', borderLeftColor: isSelected ? '#0064FF' : 'transparent', borderBottom: isLastItem ? 'none' : '1px solid #EAECF0', zIndex: isFirstItem ? 2 : 'auto', backgroundColor: isFirstItem ? (isSelected ? '#F2F7FF' : '#FFFFFF') : (isSelected ? '#F2F7FF' : 'transparent'), '&:hover': { backgroundColor: isSelected ? '#F2F7FF' : '#F9FAFB', }, ...(!isFirstItem && { pl: '44px', '&::before': { content: '""', position: 'absolute', left: '23px', top: '50%', transform: 'translate(-50%, -50%)', width: '9px', height: '9px', borderRadius: '50%', bgcolor: isSelected ? '#0064FF' : '#D0D5DD', border: isSelected ? 'none' : '2px solid white', zIndex: 3, }, '&::after': { content: '""', position: 'absolute', right: `calc(100% - 20px)`, top: '50%', transform: 'translateY(-50%)', width: '21px', height: '2px', bgcolor: isSelected ? '#0064FF' : '#EAECF0', zIndex: 2, }, }), }}>
//                     <ListItemText primary={item.name} primaryTypographyProps={{ fontWeight: 500, fontSize: '14px', color: '#1D2939' }} />
//                     <Box component="div" sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 0.5, color: '#667085', fontSize: '12px', width: '100%' }}>
//                       <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><AccessTimeIcon sx={{ fontSize: '14px' }} /> {item.time}</Box>
//                       <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><ConfirmationNumberOutlinedIcon sx={{ fontSize: '14px' }} /> {item.tokens} tokens</Box>
//                       <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><CreditCardOutlinedIcon sx={{ fontSize: '14px' }} /> ${item.cost?.toFixed(6)}</Box>
//                     </Box>
//                   </ListItem>
//                 );
//               })}
//             </List>
//           </Box>
//           {/* Right Column (Details) */}
//           <Box sx={{ overflowY: 'auto', p: 3 }}>
//             <Box sx={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', mb: 3 }}>
//               <Typography 
//                 sx={{ 
//                   fontWeight: 600, 
//                   fontFamily: 'Inter', 
//                   color: '#333333',
//                   mr: 2 ,
//                   fontSize: '16px'
//                 }}
//               >
//                 {selectedItem.name}
//               </Typography>
//               <Typography 
//                 sx={{ 
//                     fontFamily: 'Inter',
//                     fontWeight: 400,
//                     fontSize: '12px',
//                     lineHeight: '20px',
//                     color: '#666666',
//                 }}
//               >
//                 {formatTimestamp(selectedItem.timestamp)}
//               </Typography>
//             </Box>
            
//             <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
//               <InfoChip icon={AccessTimeIcon} label={`Latency: 1.19s`} />
//               <InfoChip icon={ConfirmationNumberOutlinedIcon} label={`Tokens: ${selectedItem.tokens}`} />
//               <InfoChip icon={CreditCardOutlinedIcon} label={`Cost: $${selectedItem.cost?.toFixed(6)}`} />
//             </Box>
//             <Divider sx={{ my: 3 }} />
//             <DetailCard title="Input" content={selectedItem.input} />
//             <DetailCard title="Output" contentColor="#039855" content={selectedItem.output} />
//             <DetailCard title="Metadata" content={selectedItem.metadata} />
//           </Box>
//         </Box>
//     );
//   };

//   return (
//     <Slide direction="left" in={open} mountOnEnter unmountOnExit>
//       <Box sx={{ position: 'absolute', top: 94, right: 0, bottom: 0, width: { xs: 'calc(100% - 48px)', md: '80%', lg: '70%' }, maxWidth: '900px', zIndex: 2, display: 'flex', flexDirection: 'column', boxShadow: '0 4px 12px rgba(0,0,0,0.08)', overflow: 'hidden', border: '1px solid #EAECF0', bgcolor: 'white' }}>
//         <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, bgcolor: '#F9FAFB', borderBottom: '1px solid #EAECF0' }}>
//           <Typography sx={{ fontSize: "16px", fontWeight: 600, fontFamily: 'Inter', color: '#101828' }}>Trace Details</Typography>
//           <IconButton onClick={onClose} sx={{ color: '#667085' }}><CloseIcon /></IconButton>
//         </Box>
//         {renderContent()}
//       </Box>
//     </Slide>
//   );
// };

// export default TraceDetailPanel;



// import React, { useState, useEffect } from 'react';
// import {
//   Box,
//   IconButton,
//   Typography,
//   InputAdornment,
//   TextField,
//   List,
//   ListItem,
//   ListItemText,
//   Slide,
//   Divider,
//   CircularProgress,
//   Tooltip, // Make sure to import Tooltip
// } from '@mui/material';
// import CloseIcon from '@mui/icons-material/Close';
// import ContentCopyIcon from '@mui/icons-material/ContentCopy';
// import AccessTimeIcon from '@mui/icons-material/AccessTime';
// import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
// import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
// import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
// import { gql, useQuery } from '@apollo/client';

// const GET_LLM_TRACE_DETAIL = gql`
//   query GetLlmTraceDetail($tenantId: ID!, $traceId: ID!) {
//     getLlmTraceDetail(tenantId: $tenantId, traceId: $traceId) {
//       id
//       timestamp
//       name
//       latency
//       cost
//       totalTokens
//       input
//       output
//       metadata
//       observations {
//         id
//         name
//         startTime
//         endTime
//         input
//         output
//         cost
//         totalTokens
//       }
//     }
//   }
// `;

// interface TraceDetailPanelProps {
//   open: boolean;
//   onClose: () => void;
//   tenantId?: string;
//   traceId?: string;
// }

// interface DisplayItem {
//   id: string;
//   name: string;
//   time: string;
//   tokens: number;
//   cost: number;
//   input: string | null;
//   output: string | null;
//   metadata: string;
//   timestamp: string;
// }

// const InfoChip: React.FC<{ icon: React.ElementType; label: React.ReactNode }> = ({ icon: Icon, label }) => (
//   <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, bgcolor: '#F2F4F7', borderRadius: '16px', px: 1.25, py: 0.25, border: '1px solid #EAECF0' }}>
//     <Icon sx={{ fontSize: '16px', color: '#475467' }} />
//     <Typography sx={{ fontSize: '12px', color: '#344054', fontWeight: 500, fontFamily: 'Inter' }}>{label}</Typography>
//     <InfoOutlinedIcon sx={{ fontSize: '13px', color: '#0064FF', cursor: 'pointer' }} />
//   </Box>
// );

// // --- MODIFIED DetailCard COMPONENT ---
// const DetailCard: React.FC<{ title: string; content: React.ReactNode, contentColor?: string }> = ({ title, content, contentColor = '#475467' }) => {
//   const [copied, setCopied] = useState(false);

//   const handleCopy = () => {
//     // Ensure content is a string before copying, handling null/undefined
//     const textToCopy = String(content || '');
    
//     navigator.clipboard.writeText(textToCopy).then(() => {
//       setCopied(true);
//       // Reset the "Copied!" message after 2 seconds
//       setTimeout(() => setCopied(false), 2000);
//     }).catch(err => {
//       console.error('Failed to copy text: ', err);
//     });
//   };

//   return (
//     <Box sx={{
//       border: '1px solid #EAECF0',
//       borderRadius: '12px',
//       mb: 2,
//       position: 'relative',
//       overflow: 'hidden', // Ensures inner boxes conform to the rounded corners
//     }}>
//       <Box sx={{
//           fontSize:'10px',
//         display: 'flex',
//         justifyContent: 'space-between',
//         alignItems: 'center',
//         p: '12px 16px',
//         background: '#F3F3F3', // Darker background for the header
//         borderBottom: '1px solid #EAECF0',
//       }}>
//         <Typography sx={{
//           fontWeight: 500,
//           color: '#101828',
//           fontFamily: "'Roboto Mono', monospace",
//           fontSize: '12px'
//         }}>
//           {title}
//         </Typography>
//         <Tooltip title={copied ? "Copied!" : "Copy"} placement="top">
//           <IconButton size="small" sx={{ color: '#667085' }} onClick={handleCopy}>
//             <ContentCopyIcon sx={{ fontSize: '14px',color:'#666666'}} />
//           </IconButton>
//         </Tooltip>
//       </Box>

//       <Box sx={{
//         p: '16px',
//         background: '#F9FAFB', // Lighter background for the content
//       }}>
//         <Typography sx={{
//           color: contentColor,
//           whiteSpace: 'pre-wrap',
//           fontFamily: "'Roboto Mono', monospace",
//           fontSize: '14px'
//         }}>
//           {content || 'N/A'}
//         </Typography>
//       </Box>
//     </Box>
//   );
// };
// // --- END OF MODIFIED SECTION ---

// const formatTimestamp = (isoString: string): string => {
//     if (!isoString) return '';
//     const date = new Date(isoString);
//     const formattedDate = date.toLocaleDateString('en-US', {
//         month: '2-digit',
//         day: '2-digit',
//         year: 'numeric',
//     });
//     const formattedTime = date.toLocaleTimeString('en-US', {
//         hour: 'numeric',
//         minute: '2-digit',
//         hour12: true,
//     });
//     return `${formattedDate} • ${formattedTime}`;
// };


// const TraceDetailPanel: React.FC<TraceDetailPanelProps> = ({ open, onClose, tenantId, traceId }) => {
//   const { data, loading, error } = useQuery(GET_LLM_TRACE_DETAIL, {
//     variables: { tenantId, traceId },
//     skip: !open || !tenantId || !traceId,
//   });

//   const [displayItems, setDisplayItems] = useState<DisplayItem[]>([]);
//   const [selectedItem, setSelectedItem] = useState<DisplayItem | null>(null);
//   const [searchQuery, setSearchQuery] = useState('');
  

//   useEffect(() => {
//     if (data?.getLlmTraceDetail) {
//       const mainTrace = data.getLlmTraceDetail;
      
//       const formattedMainTrace: DisplayItem = {
//         id: mainTrace.id,
//         name: mainTrace.name,
//         time: `${mainTrace.latency?.toFixed(2) ?? 0}s`,
//         tokens: mainTrace.totalTokens ?? 0,
//         cost: mainTrace.cost ?? 0,
//         input: mainTrace.input,
//         output: mainTrace.output,
//         metadata: JSON.stringify(mainTrace.metadata, null, 2),
//         timestamp: mainTrace.timestamp,
//       };

//       const formattedObservations: DisplayItem[] = (mainTrace.observations || []).map((obs: any) => {
//         const latency = (new Date(obs.endTime).getTime() - new Date(obs.startTime).getTime()) / 1000;
//         return {
//           id: obs.id,
//           name: obs.name,
//           time: `${latency.toFixed(2)}s`,
//           tokens: obs.totalTokens,
//           cost: obs.cost ?? 0,
//           input: obs.input,
//           output: obs.output,
//           metadata: 'N/A for individual observations',
//           timestamp: obs.startTime,
//         };
//       });

//       const allItems = [formattedMainTrace, ...formattedObservations];
//       setDisplayItems(allItems);
//       setSelectedItem(allItems[0]);
//     }
//   }, [data]);

//   const renderContent = () => {
//     if (loading) {
//       return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}><CircularProgress /></Box>;
//     }
//     if (error) {
//       return <Box sx={{ p: 3 }}><Typography color="error">Error: {error.message}</Typography></Box>;
//     }
//     if (!selectedItem) {
//       return null;
//     }

//     return (
//         <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '320px 1fr' }, flexGrow: 1, overflow: 'hidden', bgcolor: '#FFFFFF' }}>
//           {/* Left Column (Trace List) */}
//           <Box sx={{ display: 'flex', flexDirection: 'column', borderRight: '1px solid #EAECF0', overflow: 'hidden' }}>
//             <Box sx={{ p: 2, borderBottom: '1px solid #EAECF0' }}>
//             <TextField variant="outlined" size="small" placeholder="Search by trace name..." value={searchQuery} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)} sx={{ minWidth: '250px', '& .MuiOutlinedInput-root': { borderRadius: '10px', fontFamily: 'Inter', fontSize: '14px', height: '32px', backgroundColor: 'rgba(0, 0, 0, 0.04)', color: '#232324', fontWeight: '400', '& fieldset': { border: 0.2 } }, '& .MuiInputBase-input::placeholder': { opacity: 1, fontWeight:300, fontSize: '14px', color: '#999999' } }} InputProps={{ startAdornment: ( <InputAdornment position="start"><img src="/icons/search_icon.svg" alt="Search" style={{ width: 17, height: 17 }} /></InputAdornment>)}} />
//             </Box>
            
//             <List sx={{ overflowY: 'auto', p: 0, position: 'relative' }}>
//               <Box sx={{ position: 'absolute', top: '28px', bottom: '28px', left: '23px', width: '1px', bgcolor: '#EAECF0', zIndex: 1 }} />
//               {displayItems.map((item: DisplayItem, index: number) => {
//                 const isSelected = selectedItem.id === item.id;
//                 const isFirstItem = index === 0;
//                 const isLastItem = index === displayItems.length - 1;

//                 return (
//                   <ListItem key={item.id} onClick={() => setSelectedItem(item)} sx={{ flexDirection: 'column', alignItems: 'flex-start', cursor: 'pointer', position: 'relative', py: 1.5, px: 2, borderLeft: '3px solid', borderLeftColor: isSelected ? '#0064FF' : 'transparent', borderBottom: isLastItem ? 'none' : '1px solid #EAECF0', zIndex: isFirstItem ? 2 : 'auto', backgroundColor: isFirstItem ? (isSelected ? '#F2F7FF' : '#FFFFFF') : (isSelected ? '#F2F7FF' : 'transparent'), '&:hover': { backgroundColor: isSelected ? '#F2F7FF' : '#F9FAFB', }, ...(!isFirstItem && { pl: '44px', '&::before': { content: '""', position: 'absolute', left: '23px', top: '50%', transform: 'translate(-50%, -50%)', width: '9px', height: '9px', borderRadius: '50%', bgcolor: isSelected ? '#0064FF' : '#D0D5DD', border: isSelected ? 'none' : '2px solid white', zIndex: 3, }, '&::after': { content: '""', position: 'absolute', right: `calc(100% - 20px)`, top: '50%', transform: 'translateY(-50%)', width: '21px', height: '2px', bgcolor: isSelected ? '#0064FF' : '#EAECF0', zIndex: 2, }, }), }}>
//                     <ListItemText primary={item.name} primaryTypographyProps={{ fontWeight: 500, fontSize: '14px', color: '#1D2939' }} />
//                     <Box component="div" sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 0.5, color: '#667085', fontSize: '12px', width: '100%' }}>
//                       <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><AccessTimeIcon sx={{ fontSize: '14px' }} /> {item.time}</Box>
//                       <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><ConfirmationNumberOutlinedIcon sx={{ fontSize: '14px' }} /> {item.tokens} tokens</Box>
//                       <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><CreditCardOutlinedIcon sx={{ fontSize: '14px' }} /> ${item.cost?.toFixed(6)}</Box>
//                     </Box>
//                   </ListItem>
//                 );
//               })}
//             </List>
//           </Box>
//           {/* Right Column (Details) */}
//           <Box sx={{ overflowY: 'auto', p: 3 }}>
//             <Box sx={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', mb: 3 }}>
//               <Typography 
//                 sx={{ 
//                   fontWeight: 600, 
//                   fontFamily: 'Inter', 
//                   color: '#333333',
//                   mr: 2 ,
//                   fontSize: '16px'
//                 }}
//               >
//                 {selectedItem.name}
//               </Typography>
//               <Typography 
//                 sx={{ 
//                     fontFamily: 'Inter',
//                     fontWeight: 400,
//                     fontSize: '12px',
//                     lineHeight: '20px',
//                     color: '#666666',
//                 }}
//               >
//                 {formatTimestamp(selectedItem.timestamp)}
//               </Typography>
//             </Box>
            
//             <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
//               <InfoChip icon={AccessTimeIcon} label={`Latency: 1.19s`} />
//               <InfoChip icon={ConfirmationNumberOutlinedIcon} label={`Tokens: ${selectedItem.tokens}`} />
//               <InfoChip icon={CreditCardOutlinedIcon} label={`Cost: $${selectedItem.cost?.toFixed(6)}`} />
//             </Box>
//             <Divider sx={{ my: 3 }} />
//             <DetailCard title="Input" content={selectedItem.input} />
//             <DetailCard title="Output" contentColor="#039855" content={selectedItem.output} />
//             <DetailCard title="Metadata" content={selectedItem.metadata} />
//           </Box>
//         </Box>
//     );
//   };

//   return (
//     <Slide direction="left" in={open} mountOnEnter unmountOnExit>
//       <Box sx={{ position: 'absolute', top: 94, right: 0, bottom: 0, width: { xs: 'calc(100% - 48px)', md: '80%', lg: '70%' }, maxWidth: '900px', zIndex: 2, display: 'flex', flexDirection: 'column', boxShadow: '0 4px 12px rgba(0,0,0,0.08)', overflow: 'hidden', border: '1px solid #EAECF0', bgcolor: 'white' }}>
//         <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, bgcolor: '#F9FAFB', borderBottom: '1px solid #EAECF0' }}>
//           <Typography sx={{ fontSize: "16px", fontWeight: 600, fontFamily: 'Inter', color: '#101828' }}>Trace Details</Typography>
//           <IconButton onClick={onClose} sx={{ color: '#667085' }}><CloseIcon /></IconButton>
//         </Box>
//         {renderContent()}
//       </Box>
//     </Slide>
//   );
// };

// export default TraceDetailPanel;